import { AvatarColors, ProfileCard, ProfileImage } from 'types/profile';
import {
  Country,
  PatternValidation,
  StringValidation,
  Time,
} from 'types/general.types';
import { EnumValidation } from 'types/generic.types';
import { _ContributionsLinks } from 'types/contribution/api.types';

export enum ContributorRole {
  PRODUCER = 'PRODUCER',
  ASSISTANT_PRODUCER = 'ASSISTANT_PRODUCER',
  MIXER = 'MIXER',
  MUSICIAN = 'MUSICIAN',
}

export enum WriterRole {
  COMPOSER = 'COMPOSER',
  AUTHOR = 'AUTHOR',
  ARRANGER = 'ARRANGER',
  ADAPTER = 'ADAPTER',
}

export enum WriterRoleLabel {
  COMPOSER = 'Composer',
  AUTHOR = 'Author',
  ARRANGER = 'Arranger',
  ADAPTER = 'Adapter',
}

export enum Instrument {
  GUITAR = 'GUITAR',
  BASS = 'BASS',
  DRUMS = 'DRUMS',
  VOCAL = 'VOCAL',
  VIOLA = 'VIOLA',
}

export enum ContributionClassName {
  ARTIST = 'Artist',
  SONGWRITER = 'Songwriter',
  PRODUCTION = 'Production',
  PERFORMER = 'Performer',
}

export interface Contributor {
  guid: string;
  created: Time;
  updated: Time;
  name: string;
  parent: string;
  className: ContributionClassName;
  ipiNumber?: string;
  ipnNumber?: string;
  isniNumber?: string;
  email?: string;
  writerRole?: WriterRole;
  initials?: string;
  instruments?: Instrument[];
  contributorRole?: ContributorRole;
  country?: Country;
}

// REFACTOR: Use interfaces instead of types.
export type ContributionUser = {
  profileGuid: string;
  userGuid: string;
};

export type ContributionProfile = {
  displayName: string;
  guid?: string;
  initials: string;
  profileImages?: ProfileImage[];
  subject?: string;
  avatarColors?: AvatarColors;
};

export interface Contribution {
  guid: string;
  created: Time;
  updated: Time;
  name: string;
  parent: string;
  profile?: ContributionProfile;
  createdBy: {
    profileCard: ContributionProfile;
  };
  user?: ContributionUser;
  className: ContributionClassName;
  role?: string;
  roleDetail?: string;
  location?: {
    country: Country;
    address: string;
    name: string;
  };
  date?: {
    date: string;
  };
  ipn?: number;
  ipi?: number;
  isni?: number;
}

// REFACTOR: Don't define types that are lists of types by themselves.
export type ContributionsGroupedList = {
  [group: string]: Contribution[];
};

export type ContributionsSortedList = {
  className: ContributionClassName;
  items: Contribution[];
}[];

export type ContributionsListItem = {
  user?: ContributionUser;
  profile?: ContributionProfile;
  name: string;
  parent: string;
  guid: string;
  contributions: Partial<Contribution>[];
};

export type ContributionsGroupedAndSortedList = {
  className: ContributionClassName;
  items: ContributionsListItem[];
}[];

export enum ContributionProfileType {
  ROLE = 'role',
  INSTRUMENT = 'instrument',
}

export interface ContributionValidationSchema {
  name: StringValidation;
}

export interface ContributionPatchValidationSchema {
  name: StringValidation;
  profileGuid: StringValidation;
  ipn?: PatternValidation;
  isni?: PatternValidation;
  ipi?: PatternValidation;
  role: EnumValidation | StringValidation;
  location: {
    properties: {
      address: StringValidation;
      country: EnumValidation;
      name: StringValidation;
    };
  };
}

export type ContributionClassRolesDDL = {
  [index: string]: ContributionClassRole;
};

export interface ContributionClassesAndRolesDDL {
  classRoles: ContributionClassRolesDDL;
  classes: ContributionClassRolesDDL;
  artistRoles: ContributionClassRolesDDL;
  compositionRoles: ContributionClassRolesDDL;
  productionRoles: ContributionClassRolesDDL;
  performanceRoles: ContributionClassRolesDDL;
  performanceVocals: ContributionClassRolesDDL;
  performanceGroups: ContributionClassRolesDDL;
  performanceInstruments: ContributionClassRolesDDL;
}

export interface ContributionsList {
  items?: Contribution[];
  _links?: _ContributionsLinks;
}

export interface ContributorsListItem {
  contributor: ContributionUser & { profile: ProfileCard };
  contributions: Contribution[];
}

export interface ContributionClassRole {
  key: string;
  name: string;
  parent?: string;
  description: string;
  notSelectable?: boolean;
}

export enum ContributionIdTypes {
  IPN = 'IPN',
  IPI = 'IPI',
}
